'use client';

import { CredentialType } from '#/components/ethereum/credential-type';
import {
  useIsWalletMatch,
  WalletInput,
} from '#/components/ethereum/wallet-input';
import { Button } from '#/components/ui/button';
import {
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '#/components/ui/dialog';
import { Label } from '#/components/ui/label';
import { EthereumStakingValidatorInfo } from '#/data/react-query/queries/order-list';
import { useIsCurrentEpochPastTarget } from '#/data/v2/useEthereumCurrentEpoch';
import {
  useConfiguredBlockExplorer,
  useConfiguredChainId,
} from '#/data/wagmi/configured';
import { consolidationConfig } from '#/lib/generated';
import { handleTransactionError } from '#/lib/handle-transaction-error';
import { cn } from '#/lib/utils';
import { ValidatorStatus } from '#/modules/display/validator-status';
import { captureException } from '@sentry/nextjs';
import { useActor } from '@xstate/react';
import { Translation } from 'intl-lib';
import { ExternalLink, Loader2, Send } from 'lucide-react';
import Link from 'next/link';
import { ComponentProps, useCallback, useContext } from 'react';
import { concat, Hex, parseUnits } from 'viem';
import { useSendTransaction } from 'wagmi';
import { MachineContext } from '../contexts/machine';

interface Props extends ComponentProps<'dl'> {}

export function Confirmation({ className, ...props }: Props) {
  const interpreter = useContext(MachineContext);
  const [state, dispatch] = useActor(interpreter);
  const target = state.context.target!;
  const sources = state.context.sources!;

  const isWalletMatch = useIsWalletMatch(target.withdrawaddr);

  return (
    <div className="flex flex-col gap-4">
      <DialogHeader>
        <DialogTitle>
          <Translation path="my_staking.consolidate_dialog.ConfirmAndSign.Title" />
        </DialogTitle>
        <DialogDescription>
          <Translation path="my_staking.consolidate_dialog.ConfirmAndSign.Description" />
        </DialogDescription>
      </DialogHeader>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Label>
            <Translation path="EthereumStaking.WithdrawalAddress" />
          </Label>
          <div>
            <WalletInput address={target.withdrawaddr} />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Label>
            <Translation path="my_staking.consolidate_dialog.SourceValidators" />
          </Label>
          <div className="relative overflow-hidden">
            <dl className="max-h-80 overflow-auto flex flex-col gap-2">
              {sources.map(item => (
                <ItemRenderer
                  key={item.validatorIndex}
                  item={item}
                  targetPubKey={target.pubKey}
                  disabled={!isWalletMatch}
                />
              ))}
            </dl>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Label>
            <Translation path="my_staking.consolidate_dialog.DestinationValidator" />
          </Label>
          <div className="border rounded-xl p-3 flex flex-col gap-1">
            <div className="grid grid-cols-4 items-center gap-2 text-xs text-muted-foreground">
              <div>
                <Translation path="my_staking.order.validator_column.validator" />
              </div>
              <div className="truncate">
                <Translation path="my_staking.order.validator_column.credential_type" />
              </div>
              <div>
                <Translation path="my_staking.order.validator_column.balance" />
              </div>
              <div>
                <Translation path="my_staking.order.validator_column.status" />
              </div>
            </div>
            <div className="h-6 grid grid-cols-4 items-center gap-2 text-sm font-medium">
              <div>#{target.validatorIndex}</div>
              <div>
                <CredentialType credentialType={target.credentialType} />
              </div>
              <div>{target.effectiveBalance} ETH</div>
              <div>
                <ValidatorStatus item={target} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            dispatch('PREV');
          }}
        >
          <Translation path="my_staking.Validators.Dialog.PreviousButtonLabel" />
        </Button>
        <DialogClose asChild>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              dispatch('NEXT');
            }}
          >
            <Translation path="Application.Dialog.CloseButtonLabel" />
          </Button>
        </DialogClose>
      </DialogFooter>
    </div>
  );
}

interface ItemRendererProps {
  item: EthereumStakingValidatorInfo;
  targetPubKey: Hex;
  disabled?: boolean;
}
function ItemRenderer({ item, targetPubKey, disabled }: ItemRendererProps) {
  const chainId = useConfiguredChainId();
  const blockExplorer = useConfiguredBlockExplorer();
  const {
    sendTransactionAsync,
    data: transactionHash,
    isSuccess,
    isPending,
  } = useSendTransaction();
  const sourcePubKey = item.pubKey;

  const isConsolidatable = useIsCurrentEpochPastTarget(item.activeEpoch, 256);

  const onSignClick = useCallback(async () => {
    const data = concat([sourcePubKey, targetPubKey]); // source + target

    console.log('====================================');
    console.log('consolidation');
    console.log({ sourcePubKey, targetPubKey, data });
    console.log('====================================');

    try {
      const transactionHash = await sendTransactionAsync({
        // chainId,
        // account: address,
        to: consolidationConfig.address(chainId),
        value: parseUnits('1', 0), // 1wei
        data,
        // gas: estimatedGasLimit,
      });
    } catch (error) {
      handleTransactionError(error);
      captureException(error);
    }
  }, [sourcePubKey]);
  return (
    <div className="border border-gray-200 p-3 rounded-xl grid grid-cols-4 items-center gap-2">
      <div className="col-span-3 flex flex-col gap-1">
        <dt className="grid grid-cols-3 items-center gap-2 text-xs text-muted-foreground">
          <div>
            <Translation path="my_staking.order.validator_column.validator" />
          </div>
          <div className="truncate">
            <Translation path="my_staking.order.validator_column.credential_type" />
          </div>
          <div>
            <Translation path="my_staking.order.validator_column.balance" />
          </div>
        </dt>
        <dd
          className={cn(
            'grid grid-cols-3 gap-2 text-sm items-center rounded-md'
          )}
        >
          <div className="flex items-center gap-2">#{item.validatorIndex}</div>
          <div>
            <CredentialType credentialType={item.credentialType} />
          </div>
          <div>{item.effectiveBalance} ETH</div>
        </dd>
      </div>
      <div className="shrink-0 w-32 flex justify-end">
        {isSuccess ? (
          <Button
            type="button"
            asChild
            variant="link"
            className="text-green-600"
          >
            <Link
              href={`${blockExplorer?.url}/tx/${transactionHash}`}
              target="_blank"
              prefetch={false}
            >
              <ExternalLink />
              <Translation
                path="global.noun.view_on_button_label"
                params={blockExplorer}
              />
            </Link>
          </Button>
        ) : (
          <Button
            type="button"
            variant="primary"
            disabled={!isConsolidatable || disabled || isPending}
            onClick={onSignClick}
            className="w-full"
          >
            {isPending ? <Loader2 className="animate-spin" /> : <Send />}
            Sign
          </Button>
        )}
      </div>
    </div>
  );
}
